import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * The small pieces every Finance panel is built from.
 *
 * EVERY light container here sets an explicit text colour ON THE CONTAINER.
 * `index.html` puts `text-white` on <body>, so a white card without one renders
 * white-on-white — and it is the stray <span> deep inside that gets missed, not
 * the heading. Setting it on the container makes the whole subtree safe.
 */

export const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`bg-white text-stone-800 rounded-2xl border border-stone-200/70 shadow-sm ${className}`}>
    {children}
  </div>
);

export const CardHeader: React.FC<{
  icon?: React.ReactNode;
  title: string;
  hint?: string;
  actions?: React.ReactNode;
}> = ({ icon, title, hint, actions }) => (
  <div className="px-4 py-3 border-b border-stone-200/70 flex items-center justify-between gap-3 flex-wrap text-stone-800">
    <div className="flex items-center gap-2 min-w-0">
      {icon && <span className="text-stone-500 shrink-0">{icon}</span>}
      <h3 className="font-semibold text-sm text-stone-800 truncate">{title}</h3>
      {hint && <span className="text-xs text-stone-500 truncate">{hint}</span>}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

export const Kpi: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'muted' | 'warn';
}> = ({ label, value, sub, tone = 'default' }) => (
  <div className="bg-white text-stone-800 rounded-2xl border border-stone-200/70 p-4">
    <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
    <div className={`text-2xl font-bold tabular-nums mt-1 ${
      tone === 'muted' ? 'text-stone-400' : tone === 'warn' ? 'text-amber-600' : 'text-stone-900'
    }`}>{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-0.5">{sub}</div>}
  </div>
);

/** Whole class strings so Tailwind's scanner sees them — never interpolate these. */
const CHIP_TONE: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  blue: 'bg-blue-100 text-blue-700',
  violet: 'bg-rose-100 text-rose-700',
  stone: 'bg-stone-200 text-stone-700',
};

export const Chip: React.FC<{ tone?: keyof typeof CHIP_TONE | string; children: React.ReactNode; title?: string }> = ({
  tone = 'stone', children, title,
}) => (
  <span title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${CHIP_TONE[tone] || CHIP_TONE.stone}`}>
    {children}
  </span>
);

export const LOCATION_STATUS_TONE: Record<string, string> = {
  active: 'emerald',
  pending: 'amber',
  info_requested: 'amber',
  payment_pending: 'amber',
  deactivation_requested: 'amber',
  grace: 'red',
  rejected: 'red',
  inactive: 'stone',
};

export const Note: React.FC<{ tone?: 'info' | 'warn' | 'danger' | 'success'; children: React.ReactNode }> = ({
  tone = 'info', children,
}) => {
  const cls = tone === 'warn'
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : tone === 'danger'
      ? 'bg-red-50 border-red-200 text-red-900'
      : tone === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
        : 'bg-blue-50 border-blue-200 text-blue-900';
  return <div className={`rounded-xl border px-3.5 py-2.5 text-xs leading-relaxed ${cls}`}>{children}</div>;
};

export const Empty: React.FC<{ icon: React.ReactNode; title: string; text?: string }> = ({ icon, title, text }) => (
  <div className="px-4 py-10 text-center text-stone-500">
    <div className="mx-auto mb-3 opacity-30 flex justify-center">{icon}</div>
    <p className="text-sm text-stone-600">{title}</p>
    {text && <p className="text-xs mt-1 text-stone-500 max-w-md mx-auto leading-relaxed">{text}</p>}
  </div>
);

/**
 * A modal confirmation. There is no `window.confirm` anywhere in this app —
 * it is silently a no-op inside the iOS webview, so an operator would click
 * "waive" and nothing would happen with no explanation.
 */
export const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  tone?: 'danger' | 'default';
  confirmLabel: string;
  busy?: boolean;
  /** Non-null disables Confirm and is shown as the reason. */
  blocked?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, title, tone = 'default', confirmLabel, busy, blocked, onConfirm, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white text-stone-800 rounded-2xl border border-stone-200 shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold text-stone-900">{title}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 p-1 -m-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 text-sm text-stone-700">{children}</div>
        {blocked && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{blocked}</span>
          </div>
        )}
        <div className="flex gap-3 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !!blocked}
            className={`flex-1 px-4 py-3 text-white font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] ${
              tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-stone-900 hover:bg-black'
            }`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * THE ONE MODAL SHAPE THIS APP USES (CLAUDE.md, super-admin-ui is ruby-on-light):
 * black header + ruby gradient underline + ruby icon tile, white body, black
 * primary button. There is no "danger" tone — Ruby UI is black/white first,
 * red only ever as the gem/animated ruby light, never as a fill or a button
 * (the rule this component exists to stop a page from re-breaking). A confirm
 * that genuinely needs to read as dangerous says so in its body copy, not in
 * a red button.
 *
 * Originally written three times over (Task 34: once each in
 * EstateBusinessDetailPage, EstateStripeSettingsPage, and inline in
 * EstateFeePlansPage) before being pulled out here — this is the shared home
 * for any future "set X" / "confirm Y" dialog on a ruby-on-light page, not
 * just the Estate section.
 */
export const RubyModal: React.FC<{
  open: boolean;
  icon: React.ReactNode;
  title: string;
  onClose: () => void;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
  confirmDisabled?: boolean;
  /** Extra classes on the white card — e.g. a wider max-width or a scrolling editor body. */
  className?: string;
  /**
   * 'white' = the newer popup header (owner 2026-10-18: "all the pop-up headers
   * white, not black"). Default stays 'black' so existing callers do not change.
   */
  header?: 'black' | 'white';
  children: React.ReactNode;
}> = ({ open, icon, title, onClose, confirmLabel, onConfirm, busy, confirmDisabled, className = 'max-w-lg', header = 'black', children }) => {
  if (!open) return null;
  const white = header === 'white';
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-xl w-full overflow-hidden max-h-[90vh] flex flex-col ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`${white ? 'bg-white' : 'bg-black'} px-5 py-4 shrink-0`}>
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: white ? '#FFF1F3' : 'rgba(220,20,60,0.18)' }}
            >
              {icon}
            </div>
            <h3 className={`${white ? 'text-stone-900' : 'text-white'} font-semibold`}>{title}</h3>
          </div>
        </div>
        <div style={{ background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' }} className="shrink-0" />
        <div className="p-5 space-y-4 text-stone-800 text-sm overflow-y-auto flex-1">{children}</div>
        <div className="flex gap-3 p-5 pt-3 border-t border-stone-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            className="flex-1 px-4 py-3 bg-black text-white font-bold rounded-xl hover:bg-stone-900 disabled:opacity-40 min-h-[44px]"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
